// Browser-only image preparation, shared by the landing demo and the cabinet's
// upload page.
//
// It lives apart from the callers because of what it needs: `createImageBitmap`,
// a `<canvas>` and `canvas.toDataURL` are browser APIs with no counterpart in
// the test runner's environment, so nothing in this file can be exercised in
// process. Keeping it separate lets the logic around it be measured fully
// instead of being dragged down by code that is only ever proven in a real
// browser — which it is, by the upload paths in tests/e2e/specs. The one part
// that is plain arithmetic, choosing the output size, lives in sizing.ts and is
// measured there.

import { base64FromBuffer, blobFromBase64 } from "./base64.ts";
import { fitWithin, JPEG_QUALITY, MAX_DIMENSION } from "./sizing.ts";

export interface PreparedImage {
  // The bytes to send. A form posts this; a JSON caller sends `base64`.
  readonly blob: Blob;
  // The same bytes base64-encoded, which is how the API takes an image.
  readonly base64: string;
  // A data URL for the on-screen preview.
  readonly dataUrl: string;
}

// Shrinks the picked image in the browser before upload: a phone photo is
// tens of megabytes, and a 1600px JPEG is a fraction of that, which is the
// single biggest thing a caller can do for latency — and what keeps an accepted
// file inside every limit between here and the recognition engine.
//
// If the browser cannot decode the image to a canvas, the original file is
// returned unchanged so the upload still works. That fallback is why the limits
// downstream are sized for the whole accepted file rather than for the
// re-encoded one.
export async function prepareImage(
  file: File,
  maxDimension = MAX_DIMENSION,
  quality = JPEG_QUALITY,
): Promise<PreparedImage> {
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = fitWithin(bitmap, maxDimension);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      // Caught by this function's own catch, which sends the file unchanged.
      throw new Error("This browser cannot process the image.");
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
    return { blob: blobFromBase64(base64, "image/jpeg"), base64, dataUrl };
  } catch {
    // The original File is already a Blob, so the bytes are not copied again —
    // which matters here, where they can be the full accepted size.
    const base64 = base64FromBuffer(await file.arrayBuffer());
    return { blob: file, base64, dataUrl: `data:${file.type};base64,${base64}` };
  }
}
